import React, { useImperativeHandle, forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import moment from 'moment';
import DatePicker from "react-datepicker";

import ApplicationLayout from '../../../layouts/ApplicationLayout';
import PageHeader from '../../includes/PageHeader';
import TapIcon from '../../../services/TapIcon';
import Loader from '../../../components/ui/Loader/Loader';
import HttpAPICall from '../../../services/HttpAPICall';
import TapSelect from '../../../components/ui/TapSelect';
import TapInputNumber from '../../../components/ui/TapInputNumber';
import DateService from '../../../services/DateService';
import TapHelper from '../../../services/TapHelper';
import NumberFormatter from '../../../services/NumberFormater';
import { ORG_AUTH_URL } from '../../../services/TapApiUrls';

const PASSCODE_ADD_URL = ORG_AUTH_URL + '/passcode/add';
const PASSCODE_EDIT_URL = ORG_AUTH_URL + '/passcode/edit';
const PASSCODE_DELETE_URL = ORG_AUTH_URL + '/passcode/delete';
const PASSCODE_FORM_DATA_URL = ORG_AUTH_URL + '/passcode/form_data';

const initFormData = {
    data_loaded: false,
    loading: false,
    submitting: false,
    id: null,
    passcode: '',
    expiry_date: null,
    visibility: [],
    site_ids: [],
    site_options: [],
    visibility_options: []
};

const PasscodeAdd = forwardRef((props, ref) => {
    const history = useHistory();
    const access_token = useSelector(state => state.auth.access_token);
    const iamUserSite = useSelector(state => state.app.user_sites);
    const [formData, setFormData] = useState({ ...initFormData });
    const [isEditMode, setIsEditMode] = useState(false);
    useEffect(() => {
        loadFormData();
    }, []);
    useEffect(() => {
        if (
            formData.data_loaded &&
            props.location?.state?.editData
        ) {
            populateEditData(props.location.state.editData);
            setIsEditMode(true);
        }
    }, [formData.data_loaded]);

    const loadFormData = () => {
        setFormData(prev => ({ ...prev, loading: true, data_loaded: false }));
        HttpAPICall.withAthorization(
            'GET',
            PASSCODE_FORM_DATA_URL,
            access_token,
            {},
            {},
            (response) => {

                console.log(response);

                const visibilityOptions = Object.entries(response.data).map(([key, label]) => ({
                    value: key,
                    label
                }));

                console.log("visibilityOptions", visibilityOptions);

                setFormData(prev => ({
                    ...prev,
                    data_loaded: true,
                    site_options: iamUserSite.map(s => ({
                        label: s.site_name,
                        value: s.id
                    })),
                    visibility_options: visibilityOptions
                }));
            }
        ).finally(() => {
            setFormData(prev => ({ ...prev, loading: false }));
            if (props.location?.state?.editData) {
                const editData = props.location.state.editData;
                populateEditData(editData);
            }
        });
    };

    const populateEditData = (data) => {
        setFormData(prev => ({
            ...prev,
            id: data.id,
            passcode: data.passcode,
            expiry_date: new Date(data.expiry_date),
            visibility: data.visibility || [],
            site_ids: data.site_data?.map(site => site.id) || []
        }));
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleVisibilityChange = (selectedOptions) => {
        const values = selectedOptions?.map(opt => opt.value) || [];
        setFormData(prev => ({ ...prev, visibility: values }));
    };
    const handleSiteChange = (selectedOptions) => {
        const values = selectedOptions?.map(opt => opt.value) || [];
        setFormData(prev => ({ ...prev, site_ids: values }));
    };

    // Validation
    const validateForm = () => {
        const errors = [];

        if (!formData.passcode || formData.passcode.trim() === '') {
            errors.push('Please enter Passcode.');
        }
        if (!formData.expiry_date) {
            errors.push('Please select Expiry Date.');
        }
        if (!formData.visibility || formData.visibility.length === 0) {
            errors.push('Please select at least one Visibility option.');
        }


        return errors;
    };

    // Submit Handler
    const handleSubmit = (event) => {
        event.preventDefault();

        const errors = validateForm();
        if (errors.length > 0) {
            errors.forEach(error => {
                toast.error(error, { position: toast.POSITION.TOP_RIGHT });
            });
            return;
        }

        const payload = {
            passcode: formData.passcode,
            expiry_date: moment(formData.expiry_date).format('YYYY-MM-DD'),
            visibility: formData.visibility,
            site_ids: formData.site_ids
        };
        setFormData(prev => ({ ...prev, submitting: true }));

        const url = isEditMode ? PASSCODE_EDIT_URL : PASSCODE_ADD_URL;
        const method = isEditMode ? 'put' : 'POST';

        if (isEditMode) {
            payload.id = formData.id;
        }

        HttpAPICall.withAthorization(method, url, access_token, {}, payload, (response) => {
            toast.success(response.data.msg || 'Passcode saved successfully!', {
                position: toast.POSITION.TOP_RIGHT
            });
            handleClose();
        }).finally(() => {
            setFormData(prev => ({ ...prev, submitting: false }));
        });
    };


    const handleClose = () => {
        const state = {};
        if (props.location?.state?.listing_page_number) {
            state.page_number = props.location.state.listing_page_number;
        }
        history.push({ pathname: '/passcode', state });
    };

    const topRightHeaderJsx = () => (
        <button
            type="button"
            className="btn btn-secondary"
            disabled={formData.loading || formData.submitting}
            onClick={handleClose}
        >
            <TapIcon.imageIcon icon={TapIcon.CloseIcon} />
        </button>
    );

    const renderForm = () => (
        <form className="bg-white p-3" onSubmit={handleSubmit}>
            <div className="row align-items-center my-3">
                <label className="col-sm-3 form-label require">Passcode</label>
                <div className="col-sm-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Passcode"
                        value={formData.passcode}
                        onChange={(e) => handleFormChange('passcode', e.target.value)}
                        required
                        disabled={formData.submitting}
                    />
                </div>
            </div>

            {/* Expiry Date */}
            <div className="row align-items-center my-3">
                <label className="col-sm-3 form-label require">Expiry Date</label>
                <div className="col-sm-6 add_calender_section">
                    <DatePicker
                        selected={formData.expiry_date}
                        onChange={date => handleFormChange('expiry_date', date)}
                        dateFormat="dd-MMM-yyyy"
                        className="form-control"
                        showMonthDropdown
                        showYearDropdown
                        autoComplete="off"
                        scrollMonthYearDropdown
                        required
                        minDate={new Date()}
                        placeholderText="Select Expiry Date"
                        disabled={formData.submitting}
                    />
                    <TapIcon.imageIcon icon={TapIcon.CalenderIcon} className="add_calender-icon" />
                </div>
            </div>

            <div className="row align-items-center my-3">
                <label className="col-sm-3 form-label require">Visibility</label>
                <div className="col-sm-6">
                    <TapSelect
                        isMulti
                        placeholder="Select Visibility Options"
                        isSearchable={true}
                        required
                        options={formData.visibility_options}
                        value={formData.visibility_options?.filter(
                            opt => formData.visibility?.includes(opt.value)
                        )}
                        changeEvent={handleVisibilityChange}
                        isDisabled={formData.submitting}
                    />
                </div>
            </div>
            <div className="row align-items-center my-3">
                <label className="col-sm-3 form-label ">Sites</label>
                <div className="col-sm-6">
                    <TapSelect
                        isMulti
                        placeholder="Select Sites"
                        isSearchable={true}
                        options={formData.site_options}
                        value={formData.site_options?.filter(
                            opt => formData.site_ids?.includes(opt.value)
                        )}
                        changeEvent={handleSiteChange}
                        isDisabled={formData.submitting}
                    />

                </div>
            </div>

            <div className="col-12 text-end fixed_footer">
                <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-secondary"
                    disabled={formData.loading || formData.submitting}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary ms-1"
                    disabled={formData.loading || formData.submitting}
                >
                    {isEditMode ? 'Update' : 'Save'}
                    {formData.submitting && (
                        <TapIcon.FontAwesomeIcon icon={TapIcon.faSyncAlt} className="fa-spin ms-2" />
                    )}
                </button>
            </div>
        </form>
    );

    // Main Render
    return (
        <ApplicationLayout>
            <PageHeader
                title="Passcode Management"
                name={formData.id ? `Edit Passcode: ${formData.id}` : "Add New Passcode"}
                rightTopHeadingJsx={topRightHeaderJsx()}
            />

            <div className="container-fluid pl5">
                <div className="page_containt row">
                    <div className="pageTbl col-sm-12">
                        {formData.loading ? (
                            <div className="text-center bg-white py-5">
                                <Loader />
                            </div>
                        ) : (
                            renderForm()
                        )}
                    </div>
                </div>
            </div>
        </ApplicationLayout>
    );
});

export default PasscodeAdd;